// copyright (c) 2025 embyt GmbH. all rights reserved.
import * as React from "react";
import { connect } from "react-redux";
import {
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  LineController,
  LineElement,
  PointElement,
  LinearScale,
  TimeScale,
} from "chart.js";
import { defineMessages, WrappedComponentProps, injectIntl } from "react-intl";
import "chartjs-adapter-luxon"; // needed to support chartjs time series graphs
import { Chart } from "react-chartjs-2";
import { DateTime } from "luxon";

import { IState } from "states/states";
import { ITrendData } from "states/trendingstates";
import { CustomDashboard, ITariffCircuit, PowerUnit } from "parameter";
import { isSet } from "utils";
import { id2Index } from "guiutils";

// register used chartjs elements
ChartJS.register(LineController, LineElement, PointElement, LinearScale, TimeScale);

// uses webpack definitions
declare const IS_CLOUD: boolean;

interface ICustomDashboardEnergyGraphProps {
  settings: CustomDashboard | undefined;
  trendData: ITrendData;
  tariffCircuits: ITariffCircuit[];
  powerUnit: PowerUnit;
}

const mapStateToProps = (state: IState) => {
  const site = state.site.siteList?.find((curSite) => curSite.id === state.cached.selectedSite);
  // settings may be stored in cloud or in local parameter set
  // cloud settings have priority over local settings
  const settings =
    (site?.cloudSettings?.customDashboard as CustomDashboard | undefined) ??
    state.parameter?.general.customSettings?.customDashboard;

  // collect all used tariff circuit IDs
  const usedIds: number[] = [];
  if (settings?.tariffCircuits?.gridExport)
    usedIds.push(...settings.tariffCircuits.gridExport.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.gridImport)
    usedIds.push(...settings.tariffCircuits.gridImport.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.pv)
    usedIds.push(...settings.tariffCircuits.pv.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.battery)
    usedIds.push(...settings.tariffCircuits.battery.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.custom1)
    usedIds.push(...settings.tariffCircuits.custom1.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.custom2)
    usedIds.push(...settings.tariffCircuits.custom2.map((id) => Math.abs(id)));
  if (settings?.tariffCircuits?.custom3)
    usedIds.push(...settings.tariffCircuits.custom3.map((id) => Math.abs(id)));

  // extract powerUnit from used tariff circuits using reduce
  const powerUnit = usedIds.reduce<PowerUnit | undefined>((unit, id) => {
    if (unit) return unit; // if we already found a unit, return it
    const index = id2Index(state.parameter?.tariffline, id);
    if (isSet(index) && state.parameter?.tariffline[index].unit) {
      return state.parameter.tariffline[index].unit;
    }
    return undefined;
  }, undefined);

  const props: ICustomDashboardEnergyGraphProps = {
    settings,
    trendData: state.trending.trendData,
    tariffCircuits: state.parameter?.tariffline ?? [],
    powerUnit: powerUnit ?? PowerUnit.KWH,
  };
  return props;
};

const messages = defineMessages({
  yaxisTitle: {
    id: "Energy [kWh]",
  },
});

class CustomDashboardEnergyGraphComponent extends React.Component<
  ICustomDashboardEnergyGraphProps & WrappedComponentProps
> {
  private chartRef: any = React.createRef();
  private aggregation: "NONE" | "HOUR" | "DAY" | "MONTH" = "NONE";
  private timestamps: number[] = [];

  render() {
    this.aggregation = this.props.settings?.graph?.aggregation ?? "NONE";
    const timerange = (IS_CLOUD ? this.props.settings?.timerange ?? 7 : 1) - 1;
    const startTimestamp = DateTime.local().minus({ days: timerange }).startOf("day").toSeconds();
    // get all timestamps within the date range, with appropriate aggregation
    this.timestamps = this.getAggregatedTimestamps(startTimestamp);

    return (
      <div className="sidebar__energy-graph">
        <Chart
          ref={this.chartRef}
          type={this.props.settings?.graph?.type ?? "line"}
          data={this.createGraphData()}
          options={this.createGraphOptions()}
        />
      </div>
    );
  }

  private createGraphData(): ChartData<"line"> {
    const datasets = [];
    const tcSettings = this.props.settings?.tariffCircuits;
    const graphSettings = this.props.settings?.graph;

    // process grid data (import and export)
    if (
      (tcSettings?.gridImport && tcSettings.gridImport.length > 0) ||
      (tcSettings?.gridExport && tcSettings.gridExport.length > 0)
    ) {
      const data = this.props.settings?.graph?.separateGridImport
        ? this.processEnergyData(tcSettings?.gridImport ?? [], [], true)
        : this.processEnergyData(tcSettings?.gridImport ?? [], tcSettings?.gridExport ?? [], true);
      datasets.push({
        label: "Grid",
        data,
        borderColor: "#00cfe8", // --color-import from CustomDashboard.scss
        backgroundColor: "#00cfe8",
        tension: 0.4,
        pointRadius: 0,
      });
      if (
        this.props.settings?.graph?.separateGridImport &&
        tcSettings?.gridExport &&
        tcSettings.gridExport.length > 0
      ) {
        // add grid export as separate dataset
        const exportData = this.processEnergyData([], tcSettings.gridExport, true);
        datasets.push({
          label: "Grid Export",
          data: exportData,
          borderColor: "#46eaa1", // color-export
          backgroundColor: "#46eaa1",
          tension: 0.4,
          pointRadius: 0,
        });
      }
    }

    // process battery data (signed values)
    if (tcSettings?.battery && tcSettings.battery.length > 0) {
      const data = this.processEnergyData(tcSettings.battery);
      datasets.push({
        label: "Battery",
        data,
        borderColor: "#28c76f", // --color-pv from CustomDashboard.scss
        backgroundColor: "#28c76f",
        tension: 0.4,
        pointRadius: 0,
      });
    }

    // process PV data
    if (tcSettings?.pv && tcSettings.pv.length > 0) {
      // invert pv ids
      const pvIds = tcSettings.pv.map((id) => -id);
      const data = this.processEnergyData(pvIds);
      datasets.push({
        label: "PV",
        data,
        borderColor: "#ff9f43", // --color-pv from CustomDashboard.scss
        backgroundColor: "#ff9f43",
        tension: 0.4,
        pointRadius: 0,
      });
    }

    // process custom1 / charger data
    if (tcSettings?.custom1 && tcSettings.custom1.length > 0 && !graphSettings?.hideCustom1) {
      const data = this.processEnergyData(tcSettings.custom1);
      datasets.push({
        label: "Charger",
        data,
        borderColor: "#7367f0", // --color-custom1 for charger
        backgroundColor: "#7367f0",
        tension: 0.4,
        pointRadius: 0,
      });
    }

    // process custom2 data
    if (tcSettings?.custom2 && tcSettings.custom2.length > 0 && !graphSettings?.hideCustom2) {
      const data = this.processEnergyData(tcSettings.custom2);
      datasets.push({
        label: "Custom2",
        data,
        borderColor: "#e83e8c", // --color-custom2
        backgroundColor: "#e83e8c",
        tension: 0.4,
        pointRadius: 0,
      });
    }

    // process custom2 / other consumption data
    if (tcSettings?.custom3 && tcSettings.custom3.length > 0 && !graphSettings?.hideCustom3) {
      const data = this.processEnergyData(tcSettings.custom3);
      datasets.push({
        label: "Other",
        data,
        borderColor: "#e2011b", // --color-custom3 from CustomDashboard.scss
        backgroundColor: "#e2011b",
        tension: 0.4,
        pointRadius: 0,
      });
    }

    return {
      datasets,
    };
  }

  /**
   * returns timestamps with specified aggregation from the start timestamp to now
   */
  private getAggregatedTimestamps(startTimestamp: number): number[] {
    // get all available timestamps from trendData
    const availableTimestamps = Object.keys(this.props.trendData)
      .map(Number)
      .filter((ts) => ts >= startTimestamp);
    if (this.aggregation === "NONE") {
      return availableTimestamps;
    }

    // for aggregated timestamps, we can use a Set to collect unique values
    const uniqueTimestamps = new Set<number>();
    availableTimestamps.forEach((ts) => {
      // round down to the nearest hour or day based on aggregation
      let aggregatedTs: number;

      if (this.aggregation === "HOUR") {
        aggregatedTs = Math.floor(ts / 3600) * 3600;
      } else if (this.aggregation === "DAY") {
        // use Luxon to find the start of the day
        aggregatedTs = DateTime.fromSeconds(ts).startOf("day").toSeconds();
      } else if (this.aggregation === "MONTH") {
        // use Luxon to find the start of the day
        aggregatedTs = DateTime.fromSeconds(ts).startOf("month").toSeconds();
      } else {
        // default to hourly if unrecognized
        aggregatedTs = Math.floor(ts / 3600) * 3600;
      }

      // add to set (automatically handles duplicates)
      uniqueTimestamps.add(aggregatedTs);
    });

    // convert set to sorted array
    return Array.from(uniqueTimestamps).sort((a, b) => a - b);
  }

  private processEnergyData(
    tariffCircuitIds: number[],
    negTariffCircuitIds?: number[],
    onlyPositive?: boolean, // just use positive energy values as graph data (and negative for negTCs)
  ) {
    const data: { x: number; y: number }[] = [];
    this.timestamps.forEach((aggregatedTs) => {
      // find all timestamps that belong to this aggregation period
      const inPeriodTimestamps = this.getTimestampsInPeriod(aggregatedTs, this.aggregation);
      let totalEnergy = 0;

      if (inPeriodTimestamps.length > 0) {
        // calculate total from positive tariff circuit IDs
        tariffCircuitIds.forEach((id) => {
          const tcIndex = this.getTariffCircuitIndex(Math.abs(id));
          const doInvert = id < 0; // invert energy for negative ids (e.g. PV)

          // directly sum energy values into totalEnergy
          inPeriodTimestamps.forEach((timestamp) => {
            if (isSet(tcIndex) && isSet(this.props.trendData[timestamp][tcIndex]?.energy)) {
              const energy = (doInvert ? -1 : 1) * this.props.trendData[timestamp][tcIndex].energy;
              if (onlyPositive) {
                totalEnergy += Math.max(0, energy);
              } else {
                totalEnergy += energy;
              }
            }
          });
        });

        // calculate total from negative tariff circuit IDs
        negTariffCircuitIds?.forEach((id) => {
          const tcIndex = this.getTariffCircuitIndex(Math.abs(id));
          const doInvert = id < 0; // invert energy for negative ids (e.g. PV)

          inPeriodTimestamps.forEach((timestamp) => {
            if (isSet(tcIndex) && isSet(this.props.trendData[timestamp][tcIndex]?.energy)) {
              const energy = (doInvert ? 1 : -1) * this.props.trendData[timestamp][tcIndex].energy;
              totalEnergy += onlyPositive ? Math.min(0, energy) : energy;
            }
          });
        });

        data.push({
          x: aggregatedTs * 1000, // convert to milliseconds for Chart.js
          y: this.props.settings?.graph?.showAllPositive ? Math.abs(totalEnergy) : totalEnergy,
        });
      }
    });

    return data;
  }

  /**
   * returns all timestamps from trendData that fall within the specified aggregation period
   */
  private getTimestampsInPeriod(aggregatedTimestamp: number, aggregation: string): number[] {
    // get all available timestamps from trendData
    return Object.keys(this.props.trendData)
      .map(Number)
      .filter((ts) => {
        if (aggregation === "NONE") {
          return ts === aggregatedTimestamp;
        } else if (aggregation === "HOUR") {
          // check if timestamp belongs to the specified hour
          return ts >= aggregatedTimestamp && ts < aggregatedTimestamp + 3600;
        } else if (aggregation === "DAY") {
          // check if timestamp belongs to the specified day
          const dayStart = aggregatedTimestamp;
          const dayEnd = dayStart + 24 * 3600; // 24 hours
          return ts >= dayStart && ts < dayEnd;
        } else if (aggregation === "MONTH") {
          const monthEnd = DateTime.fromSeconds(aggregatedTimestamp).endOf("month").toSeconds();
          return ts >= aggregatedTimestamp && ts < monthEnd;
        }
        // default to hourly if unrecognized
        return ts >= aggregatedTimestamp && ts < aggregatedTimestamp + 3600;
      });
  }

  private getTariffCircuitIndex(id: number): number | undefined {
    for (let i = 0; i < this.props.tariffCircuits.length; i++) {
      if ((this.props.tariffCircuits[i].id ?? i) === id) {
        return i;
      }
    }
    return undefined;
  }

  private createGraphOptions(): ChartOptions<"line" | "bar"> {
    return {
      responsive: true,
      maintainAspectRatio: false,
      scales: {
        x: {
          type: "time",
          display: true,
          time: {
            unit: "day",
            displayFormats: {
              day: "MMM d",
            },
          },
          ticks: {
            maxRotation: 0,
            autoSkip: true,
            maxTicksLimit: 5,
            font: {
              size: 10,
            },
          },
          grid: {
            display: false,
          },
        },
        y: {
          display: true,
          beginAtZero: true,
          title: {
            display: true,
            text: this.props.intl.formatMessage(messages.yaxisTitle),
          },
          ticks: {
            font: {
              size: 10,
            },
            maxTicksLimit: 5,
          },
          grid: {
            display: true,
            color: "rgba(0, 0, 0, 0.1)",
          },
        },
      },
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          enabled: false,
        },
        datalabels: {
          display: false,
        },
      },
      elements: {
        line: {
          tension: 0.4,
        },
        point: {
          radius: 0,
          hoverRadius: 0,
        },
      },
      animation: {
        duration: 0,
      },
    };
  }
}

export default injectIntl(connect(mapStateToProps)(CustomDashboardEnergyGraphComponent));
